import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Menu, MenuItem, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LanguageIcon from '@mui/icons-material/Language';
import { changeLanguage, languageLabel, supportedLanguages } from '../../../i18n';
import { brand, radius } from './brand';

// A menu rather than a row of chips: the list grows with every locale added, and
// eight of them do not fit next to the logo on a phone.
export const LanguageMenu: FC = () => {
    const {t, i18n} = useTranslation();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    const pick = async (language: string) => {
        setAnchor(null);
        await changeLanguage(language);
    };

    return (
        <>
            <Button
                onClick={event => setAnchor(event.currentTarget)}
                aria-haspopup="menu"
                aria-expanded={Boolean(anchor)}
                aria-label={t('nav.language')}
                data-testid="lang-menu"
                startIcon={<LanguageIcon sx={{fontSize: 18}}/>}
                endIcon={<ExpandMoreIcon sx={{fontSize: 18}}/>}
                sx={{
                    border: `1px solid ${brand.border}`,
                    borderRadius: radius.pill,
                    background: brand.surface,
                    color: brand.navy,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '.85rem',
                    padding: '5px 14px',
                    '&:hover': {background: brand.backgroundAlt, borderColor: brand.accentSoft},
                }}>
                {languageLabel(i18n.language)}
            </Button>

            <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                slotProps={{paper: {sx: {borderRadius: radius.card, marginTop: '6px', minWidth: 180}}}}>
                {supportedLanguages.map(language => {
                    const active = i18n.language === language;
                    return (
                        <MenuItem
                            key={language}
                            selected={active}
                            onClick={() => pick(language)}
                            data-testid={`lang-${language}`}
                            sx={{padding: '8px 16px'}}>
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{width: '100%'}}>
                                <Typography sx={{
                                    flex: 1,
                                    color: brand.navy,
                                    fontWeight: active ? 700 : 500,
                                    fontSize: '.94rem',
                                }}>
                                    {languageLabel(language)}
                                </Typography>
                                <Typography sx={{
                                    color: brand.text,
                                    fontSize: '.74rem',
                                    fontWeight: 700,
                                    letterSpacing: '.06em',
                                    textTransform: 'uppercase',
                                }}>
                                    {language}
                                </Typography>
                                {active && <CheckIcon sx={{fontSize: 16, color: brand.blue}}/>}
                            </Stack>
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
};
